/**
 * Candidate Tracker Component
 * Excel-like grid for managing candidates in the recruitment pipeline
 */

import React, { useState, useEffect, useCallback } from "react";
import candidateService from "../shared/services/candidateService";
import jobService from "../shared/services/jobService";
import onboardingService from "../shared/services/onboardingService";
import { crmDataService } from "../../HR/CRM/utils/crmDataService";
import EditableCell from "../shared/components/EditableCell";
import CandidateForm from "./components/CandidateForm";
import { CANDIDATE_COLUMNS, CANDIDATE_STAGES } from "./utils/candidateColumns";
import "./styles/CandidateTracker.css";

function CandidateTracker() {
  const [candidates, setCandidates] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingStores, setLoadingStores] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [columnFilters, setColumnFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [jobCandidateStagesoptions, setJobCandidateStagesoptions] = useState(
    CANDIDATE_STAGES.filter(stage => !['Rejected', 'Withdrawn'].includes(stage))
  );

  // Rejection/Withdrawal Modal State
  const [rejectionModal, setRejectionModal] = useState({
    isOpen: false,
    candidateId: null,
    candidateName: '',
    targetStage: null, // 'Rejected' or 'Withdrawn'
    reasons: [],
    selectedReasonId: null
  });


  const loadStages = async () => {
    try {
      const response = await jobService.getJobStages();
      if (response.success && Array.isArray(response.data)) {
        // Filter out Rejected and Withdrawn from the dropdown options
        const stageOptions = response.data
          .filter(s => !['Rejected', 'Withdrawn'].includes(s?.stage_name) && ![15, 16].includes(s?.id))
          .map(s => s?.stage_name);
        setJobCandidateStagesoptions(stageOptions);
      }
    } catch (err) {
      console.error('Failed to load job statuses:', err);
    }
  };

  // Load rejection reasons
  const loadRejectionReasons = async (reasonType) => {
    try {
      const response = await jobService.getRejectionReasons(reasonType);
      if (response.success && Array.isArray(response.data)) {
        return response.data.filter(r => r.is_active);
      }
      return [];
    } catch (err) {
      console.error('Failed to load rejection reasons:', err);
      return [];
    }
  };

  // Load candidates and jobs on mount
  useEffect(() => {
    loadData();
    loadStores();
    loadStages();
  }, []);

  const loadStores = async () => {
    try {
      setLoadingStores(true);
      const storesData = await crmDataService.getStores();
      // Handle both {data: [...]} and [...] response formats
      const storesArray = storesData.data || storesData;
      const sortedStores = storesArray.sort((a, b) =>
        (a.store_name || '').localeCompare(b.store_name || '')
      );
      setStores(sortedStores);
    } catch (err) {
      console.error('Failed to load stores:', err);
      setStores([]);
    } finally {
      setLoadingStores(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [candidatesData, jobsData] = await Promise.all([
        candidateService.getAllCandidates(),
        jobService.getJobsDropdown(),
      ]);

      setCandidates(candidatesData.data || []);
      setJobs(jobsData.data || []);
      setError(null);
    } catch (err) {
      console.error("Error loading data:", err);
      setError("Failed to load candidates. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Get location name from store ID - wrapped in useCallback
  const getLocationName = useCallback((locationId) => {
    if (!locationId) return '';
    const store = stores.find(s => s.id === parseInt(locationId));
    return store?.store_name || '';
  }, [stores]);

  // Excel-like column filtering and sorting
  const applyFiltersAndSort = useCallback(() => {
    let filtered = [...candidates];

    // Apply column filters
    Object.keys(columnFilters).forEach(columnKey => {
      const filterValue = columnFilters[columnKey];
      if (filterValue && filterValue.trim() !== '') {
        filtered = filtered.filter(candidate => {
          let cellValue = candidate[columnKey];

          // Special handling for job_location filter - search by store name
          if (columnKey === 'job_location') {
            const storeName = getLocationName(cellValue);
            return storeName.toLowerCase().includes(filterValue.toLowerCase());
          }

          if (cellValue === null || cellValue === undefined) return false;
          return String(cellValue).toLowerCase().includes(filterValue.toLowerCase());
        });
      }
    });

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Special handling for job_location sorting - sort by store name
        if (sortConfig.key === 'job_location') {
          aValue = getLocationName(aValue);
          bValue = getLocationName(bValue);
        }

        // Handle null/undefined values
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        // Compare values
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredCandidates(filtered);
  }, [candidates, columnFilters, sortConfig, getLocationName]);

  // Apply filters and sorting whenever dependencies change
  useEffect(() => {
    applyFiltersAndSort();
  }, [applyFiltersAndSort]);

  const handleColumnFilterChange = (columnKey, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [columnKey]: value
    }));
  };

  const handleSort = (columnKey) => {
    let direction = 'asc';
    if (sortConfig.key === columnKey && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: columnKey, direction });
  };

  const clearAllFilters = () => {
    setColumnFilters({});
    setSortConfig({ key: null, direction: 'asc' });
  };

  // Handle inline cell update
  const handleCellUpdate = async (candidateId, field, value) => {
    try {
      // Special handling for candidate_stage when selecting Rejected or Withdrawn
      if (field === 'candidate_stage' && (value === 'Rejected' || value === 'Withdrawn')) {
        const candidate = candidates.find(c => c.id === candidateId);
        const reasonType = value === 'Rejected' ? 'rejected' : 'withdrawn';
        const reasons = await loadRejectionReasons(reasonType);

        setRejectionModal({
          isOpen: true,
          candidateId,
          candidateName: candidate?.candidate_name || 'this candidate',
          targetStage: value,
          reasons,
          selectedReasonId: null
        });
        return; // Don't update yet, wait for modal confirmation
      }

      // Special handling for job_location field - update the job, not the candidate
      if (field === 'job_location') {
        const candidate = candidates.find(c => c.id === candidateId);
        if (!candidate || !candidate.job_id) {
          setError("Cannot update location: candidate has no associated job");
          return;
        }

        // Optimistically update UI
        setCandidates((prev) =>
          prev.map((c) => (c.id === candidateId ? { ...c, [field]: value } : c))
        );

        // Update the job's location field (store ID)
        await jobService.updateJob(candidate.job_id, { location: value });

        // Reload to get updated location from JOIN
        await loadData();
      } else {
        // Optimistically update UI for candidate fields
        setCandidates((prev) =>
          prev.map((c) => (c.id === candidateId ? { ...c, [field]: value } : c))
        );

        // Send update to server for candidate fields
        await candidateService.updateCandidate(candidateId, { [field]: value });

        // Auto-trigger onboarding when candidate stage changes to "Hired"
        if (field === 'candidate_stage' && value === 'Hired') {
          try {
            const response = await onboardingService.createFromCandidate(candidateId);
            if (response.success) {
              setError(null);
              alert(`✅ ${response.data.employee_name} has been successfully moved to the Onboarding Tracker!`);
            }
          } catch (onboardingErr) {
            console.error('Error moving candidate to onboarding:', onboardingErr);
            // Check if it's a duplicate error
            if (onboardingErr.response?.data?.error?.includes('already has an onboarding record')) {
              alert('ℹ️ This candidate already has an onboarding record.');
            } else {
              alert('⚠️ Candidate status updated, but failed to create onboarding record. Please create it manually from the Onboarding Tracker.');
            }
          }
        }
      }
    } catch (err) {
      console.error("Error updating:", err);
      setError(`Failed to update ${field === 'job_location' ? 'location' : 'candidate'}. Please try again.`);
      // Reload data to revert optimistic update
      loadData();
    }
  };

  // Handle rejection/withdrawal confirmation
  const handleRejectionConfirm = async () => {
    const { candidateId, targetStage, selectedReasonId } = rejectionModal;

    if (!selectedReasonId) {
      alert('Please select a reason');
      return;
    }

    try {
      // Update candidate with the stage and rejection_reason_id
      // Note: We keep the last active stage_id by only updating candidate_stage (text field)
      // and storing the rejection_reason_id separately
      await candidateService.updateCandidate(candidateId, {
        candidate_stage: targetStage,
        rejection_reason_id: selectedReasonId
      });

      // Reload data to get updated info including rejection_reason_name
      await loadData();

      // Close modal
      setRejectionModal({
        isOpen: false,
        candidateId: null,
        candidateName: '',
        targetStage: null,
        reasons: [],
        selectedReasonId: null
      });
    } catch (err) {
      console.error("Error updating candidate stage:", err);
      setError("Failed to update candidate. Please try again.");
    }
  };

  // Handle opening form for new candidate
  const handleAddNew = () => {
    setEditingCandidate(null);
    setShowForm(true);
  };

  // Handle opening form for editing
  const handleEdit = (candidate) => {
    setEditingCandidate(candidate);
    setShowForm(true);
  };

  // Handle form save
  const handleFormSave = async (candidateData) => {
    try {
      if (editingCandidate) {
        await candidateService.updateCandidate(
          editingCandidate.id,
          candidateData
        );
      } else {
        await candidateService.createCandidate(candidateData);
      }
      setShowForm(false);
      setEditingCandidate(null);
      loadData();
    } catch (err) {
      console.error("Error saving candidate:", err);
      throw err;
    }
  };

  // Handle delete
  const handleDelete = async (candidateId) => {
    if (!window.confirm("Are you sure you want to delete this candidate?")) {
      return;
    }

    try {
      await candidateService.deleteCandidate(candidateId);
      loadData();
    } catch (err) {
      console.error("Error deleting candidate:", err);
      setError("Failed to delete candidate. Please try again.");
    }
  };

  if (loading || loadingStores) {
    return (
      <div className="candidate-tracker-loading">
        <div className="loading-spinner"></div>
        <p>Loading Candidate Tracker...</p>
      </div>
    );
  }

  return (
    <div className="candidate-tracker">
      <div className="tracker-header">
        <h1>Candidate Tracker</h1>
        <div className="tracker-actions">
          <button className="btn-primary" onClick={handleAddNew}>
            + Add Candidate
          </button>
          <button className="btn-secondary" onClick={loadData}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Excel-like Grid */}
      <div className="tracker-grid-container">
        <table className="tracker-grid">
          <thead>
            <tr>
              <th className="action-column" rowSpan="2">
                Actions
                {Object.keys(columnFilters).length > 0 && (
                  <button
                    className="clear-filters-btn"
                    onClick={clearAllFilters}
                    title="Clear all filters and sorting"
                  >
                    ✕
                  </button>
                )}
              </th>
              {CANDIDATE_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  style={{ minWidth: col.width, cursor: 'pointer' }}
                  onClick={() => handleSort(col.key)}
                  title={`Click to sort by ${col.label}`}
                >
                  <div className="column-header">
                    <span>
                      {col.label}
                      {col.required && <span className="required">*</span>}
                    </span>
                    <span className="sort-indicator">
                      {sortConfig.key === col.key && (
                        sortConfig.direction === 'asc' ? ' ▲' : ' ▼'
                      )}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
            <tr className="filter-row">
              {CANDIDATE_COLUMNS.map((col) => (
                <th key={`filter-${col.key}`} className="filter-cell">
                  <input
                    type="text"
                    className="column-filter-input"
                    placeholder={`Filter ${col.label}...`}
                    value={columnFilters[col.key] || ''}
                    onChange={(e) => handleColumnFilterChange(col.key, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredCandidates.length === 0 ? (
              <tr>
                <td colSpan={CANDIDATE_COLUMNS.length + 1} className="no-data">
                  {candidates.length === 0
                    ? "No candidates found. Click '+ Add Candidate' to get started."
                    : "No candidates match your filters."}
                </td>
              </tr>
            ) : (
              filteredCandidates.map((candidate) => (
                <tr key={candidate.id}>
                  <td className="action-column">
                    <button
                      className="btn-icon"
                      onClick={() => handleEdit(candidate)}
                      title="Edit in form"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => handleDelete(candidate.id)}
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </td>
                  {CANDIDATE_COLUMNS.map((col) => {
                    // Special handling for location field
                    if (col.key === 'job_location') {
                      const locationOptions = stores.map(store => ({
                        value: store.id,
                        label: store.store_name
                      }));

                      return (
                        <td key={col.key}>
                          <EditableCell
                            value={candidate[col.key]}
                            type="select"
                            options={locationOptions}
                            placeholder="Select Location"
                            onSave={(value) => handleCellUpdate(candidate.id, col.key, value)}
                          />
                        </td>
                      );
                    }

                    // Special display for candidate_stage showing rejection reason
                    if (col.key === 'candidate_stage') {
                      const isRejectedOrWithdrawn = ['Rejected', 'Withdrawn'].includes(candidate.candidate_stage);
                      const displayValue = isRejectedOrWithdrawn && candidate.rejection_reason_name
                        ? `${candidate.candidate_stage} (${candidate.rejection_reason_name})`
                        : candidate.candidate_stage;

                      return (
                        <td key={col.key} title={isRejectedOrWithdrawn && candidate.rejection_reason_name ? `Reason: ${candidate.rejection_reason_name}` : ''}>
                          <EditableCell
                            value={candidate[col.key]}
                            displayValue={displayValue}
                            type={col.type}
                            options={jobCandidateStagesoptions}
                            placeholder={col.label}
                            onSave={(value) =>
                              handleCellUpdate(candidate.id, col.key, value)
                            }
                          />
                        </td>
                      );
                    }

                    return (
                      <td key={col.key}>
                        <EditableCell
                          value={candidate[col.key]}
                          type={col.type}
                          options={col.key === 'candidate_stage' ? jobCandidateStagesoptions : col.options}
                          placeholder={col.label}
                          onSave={(value) =>
                            handleCellUpdate(candidate.id, col.key, value)
                          }
                        />
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Candidate Form Modal */}
      {showForm && (
        <CandidateForm
          candidate={editingCandidate}
          jobs={jobs}
          stores={stores}
          onSave={handleFormSave}
          onClose={() => {
            setShowForm(false);
            setEditingCandidate(null);
          }}
        />
      )}

      {/* Rejection/Withdrawal Reason Modal */}
      {rejectionModal.isOpen && (
        <div className="modal-overlay" onClick={() => setRejectionModal({ ...rejectionModal, isOpen: false })}>
          <div className="modal-content rejection-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {rejectionModal.targetStage === 'Rejected' ? '❌ Reject' : '🚪 Withdraw'} Candidate
              </h2>
              <button
                className="modal-close-btn"
                onClick={() => setRejectionModal({ ...rejectionModal, isOpen: false })}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                Please select a reason for {rejectionModal.targetStage === 'Rejected' ? 'rejecting' : 'withdrawing'}{' '}
                <strong>{rejectionModal.candidateName}</strong>:
              </p>
              <div className="form-group">
                <label>
                  {rejectionModal.targetStage === 'Rejected' ? 'Rejection' : 'Withdrawal'} Reason:
                </label>
                <select
                  value={rejectionModal.selectedReasonId || ''}
                  onChange={(e) => setRejectionModal({
                    ...rejectionModal,
                    selectedReasonId: e.target.value ? parseInt(e.target.value) : null
                  })}
                  className="reason-select"
                >
                  <option value="">-- Select a reason --</option>
                  {rejectionModal.reasons.map((reason) => (
                    <option key={reason.id} value={reason.id}>
                      {reason.reason_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setRejectionModal({ ...rejectionModal, isOpen: false })}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleRejectionConfirm}
                disabled={!rejectionModal.selectedReasonId}
                style={{
                  backgroundColor: rejectionModal.targetStage === 'Rejected' ? '#dc2626' : '#f59e0b'
                }}
              >
                Confirm {rejectionModal.targetStage === 'Rejected' ? 'Rejection' : 'Withdrawal'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CandidateTracker;
